/**
 * Unified Sample Data Registry
 *
 * Canonical source of truth for all sample data across the platform.
 * Documents attribution chains, scenario coverage, and entity connections.
 *
 * PURPOSE:
 * - QA: Validate end-to-end workflows with realistic, connected data
 * - Demo: Present meaningful dashboards and operator views
 * - R&D: Test new features against a complete dataset
 * - Onboarding: Provide a consistent baseline for new environments
 *
 * RULE: All seed files reference this registry for consistency.
 * No module should define disconnected mock data.
 */

import { suggestChannel } from "@/lib/growthChannelMapping";

// ── Attribution Chain ──────────────────────────────────────────
// Growth Channel → Lead Source → Lead (Opportunity) → Job → Revenue
//
// This is the canonical mapping. Every opportunity and job MUST
// have a leadSource that maps to a growth channel.

export const LEAD_SOURCE_CHANNEL_MAP: Record<string, string> = {
  "Facebook Ads":   "meta-growth-engine",
  "Instagram Ads":  "meta-growth-engine",
  "Google Ads":     "google-ads",
  "Angi":           "angi",
  "Angi Leads":     "angi",
  "Home Advisor":   "angi",           // HomeAdvisor is now Angi
  "Thumbtack":      "thumbtack",
  "Referral":       "referral-engine",
  "Home Show":      null as any,       // Unattributed — offline
  "TV Ad":          null as any,       // Unattributed — offline
  "Website":        null as any,       // Direct / organic
  "Yelp":           null as any,       // Not a managed channel
};

/** Resolve a lead source to its growth channel ID (null if unattributed) */
export function resolveChannel(leadSource: string): string | null {
  if (leadSource in LEAD_SOURCE_CHANNEL_MAP) {
    return LEAD_SOURCE_CHANNEL_MAP[leadSource] ?? null;
  }
  return suggestChannel(leadSource);
}

// ── Scenario Coverage ──────────────────────────────────────────
// Documents which sample records cover each required scenario.

export const SCENARIO_COVERAGE = {
  // Won outcome types
  wonOutcomes: {
    net:              ["OPP-001", "OPP-002", "OPP-020"],
    pending:          ["OPP-003"],
    canceled:         ["OPP-021"],
    finance_declined: ["OPP-022"],
  },

  // Lead quality scenarios
  leadQuality: {
    high_fit_booking:    ["OPP-001", "OPP-017"],    // Local, strong channel, good rep
    poor_fit_booking:    ["OPP-006"],                // Orlando — long distance from Tampa reps
    long_distance:       ["OPP-009", "OPP-011"],     // Jacksonville, Boca Raton
    out_of_area:         ["OPP-023"],                // Out-of-area lead
  },

  // Channel performance
  channelPerformance: {
    high_performing_meta: ["OPP-008", "OPP-015", "OPP-020", "OPP-024"],  // Facebook Ads leads
    weak_external:        ["OPP-019"],               // Angi — lower conversion
    tracked_angi:         ["OPP-019", "OPP-025"],
    tracked_thumbtack:    ["OPP-026"],
  },

  // Job lifecycle
  jobStages: {
    in_progress:         ["JOB-001"],
    qa_review:           ["JOB-004"],
    issue_blocked:       ["JOB-005"],
    completed:           ["JOB-009", "JOB-010", "JOB-018"],
    awaiting_material:   ["JOB-006"],
    scheduled:           ["JOB-011", "JOB-016"],
    ready_for_scheduling:["JOB-006"],
  },

  // Financial scenarios
  financial: {
    cash_deal:           ["JOB-001"],
    financed_deal:       ["JOB-002"],
    card_deal:           ["JOB-003"],
    mixed_deal:          ["JOB-004"],
    paid_in_full:        ["JOB-003", "JOB-010"],
    partial_payment:     ["JOB-001", "JOB-004"],
  },

  // Material scenarios
  materials: {
    ready:               ["JOB-001", "JOB-003"],
    missing_items:       ["JOB-012", "JOB-015"],
    partially_received:  ["JOB-002", "JOB-006"],
    field_purchase:      ["JOB-015"],
  },

  // QA scenarios
  qa: {
    pass:                ["JOB-009", "JOB-018"],
    fail:                ["JOB-004"],            // Glass alignment issue
  },

  // Rep performance (from repPerformanceIntelligence)
  reps: {
    high_performer:      "Mike Johnson",    // 45% net conversion
    consistent:          "Sarah Chen",      // 45% net conversion
    underperforming:     "David Williams",  // 27% net conversion
    volume_limited:      "James Rivera",    // Low volume
  },
} as const;

// ── Entity Counts ──────────────────────────────────────────────

export const ENTITY_SUMMARY = {
  opportunities: {
    total: 26,         // OPP-001 through OPP-026
    byStage: {
      lead: 4,
      appointment_scheduled: 2,
      appointment_completed: 4,
      follow_up_needed: 3,
      closed_won: 6,   // net + pending + canceled + finance_declined
      closed_lost: 3,
    },
  },
  jobs: {
    total: 18,         // JOB-001 through JOB-018
    active: 14,
    completed: 3,
    blocked: 1,
  },
  issues: {
    total: 10,
    open: 4,
    resolved: 4,
    in_progress: 2,
  },
  contractors: 5,
  reps: 5,            // Mike Johnson, Sarah Chen/Davis, Tom Baker, David Williams, Emily Parker
  growthChannels: {
    managed: 1,        // Meta Growth Engine
    tracked: 2,        // Angi, Thumbtack
    coming_soon: 2,    // Google Ads, Referral Engine
  },
} as const;

// ── Financial Summary (for dashboard coherence) ─────────────────
// These values should match what's computed from seed data.
// Use as reference when validating dashboard output.

export const FINANCIAL_REFERENCE = {
  // Total contract value from all jobs
  totalContractValue: 93_200,
  // Approximate marketing spend per channel (derived from lead costs)
  channelSpend: {
    "meta-growth-engine": 1_100,  // ~5 FB leads × $220 avg
    "google-ads": 1_400,          // ~5 Google leads × $280 avg
    "angi": 330,                  // ~2 Angi leads × $165 avg
    "thumbtack": 180,             // ~1 Thumbtack lead × $180
    "referral-engine": 0,         // Referrals are free
  },
  // CPA expectations (for dashboard validation)
  expectedCPA: {
    "meta-growth-engine": 275,    // $1,100 / 4 net jobs = ~$275
    "google-ads": 467,            // $1,400 / 3 net jobs = ~$467
    "angi": 330,                  // $330 / 1 net job = $330
  },
} as const;

// ── Attribution Chain Validator ─────────────────────────────────
// Run this in dev to verify no broken chains exist.

export function validateAttributionChains(
  opportunities: Array<{ id: string; leadSource: string; linkedJobId?: string; wonOutcome?: string }>,
  jobs: Array<{ id: string; marketing?: { leadSource: string } }>,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const opp of opportunities) {
    // Check lead source maps to a channel
    const channel = resolveChannel(opp.leadSource);
    if (!channel && !["Home Show", "TV Ad", "Website", "Yelp", "Referral"].includes(opp.leadSource)) {
      errors.push(`OPP ${opp.id}: leadSource "${opp.leadSource}" has no channel mapping`);
    }

    // Check linked job exists for won deals
    if (opp.wonOutcome === "net" && opp.linkedJobId) {
      const job = jobs.find(j => j.id === opp.linkedJobId);
      if (!job) {
        errors.push(`OPP ${opp.id}: linkedJobId "${opp.linkedJobId}" not found in jobs`);
      }
    }
  }

  // Check job marketing attribution
  for (const job of jobs) {
    if (!job.marketing?.leadSource) {
      errors.push(`JOB ${job.id}: missing marketing.leadSource`);
    }
  }

  return { valid: errors.length === 0, errors };
}
